import React from 'react';
import styles from './ChartSkeleton.module.css';

type ChartSkeletonVariant = 'activity' | 'monthly';

interface ChartSkeletonProps {
  className?: string;
  height?: number;
  variant: ChartSkeletonVariant;
}

interface PlotLayout {
  barGroups: Array<[number, number]>;
  chartRows: string;
  gutter: string;
  legendLabelWidths: number[];
  plotGutter: string;
}

const plotLayout: Record<ChartSkeletonVariant, PlotLayout> = {
  activity: {
    barGroups: [
      [38, 24],
      [52, 34],
      [44, 28],
      [62, 40],
      [48, 30],
      [70, 46],
      [50, 32],
      [60, 38],
      [46, 28],
      [58, 36],
    ],
    chartRows: '56px minmax(0, 1fr) 40px',
    gutter: 'px-16',
    legendLabelWidths: [64, 100, 104],
    plotGutter: 'px-16',
  },
  monthly: {
    barGroups: [
      [30, 18],
      [55, 36],
      [42, 26],
      [68, 48],
      [48, 32],
      [78, 54],
      [44, 28],
      [62, 42],
      [34, 20],
      [72, 50],
      [50, 34],
      [64, 44],
    ],
    chartRows: '48px minmax(0, 1fr) 28px',
    gutter: 'px-8',
    legendLabelWidths: [100, 104],
    plotGutter: 'px-8',
  },
};

function LegendItem({ labelWidth }: { labelWidth: number }): React.ReactElement {
  return (
    <span className="flex h-3 items-center gap-2">
      <span className={`h-2 w-2 shrink-0 rounded-sm ${styles.shimmer}`} />
      <span className={`h-2 shrink-0 rounded-sm ${styles.shimmer}`} style={{ width: labelWidth }} />
    </span>
  );
}

function PlotGrid(): React.ReactElement {
  return (
    <div className="pointer-events-none absolute inset-0 flex flex-col justify-between" aria-hidden>
      {Array.from({ length: 5 }).map((_, index) => (
        <span className="block h-px w-full bg-bzgray-200" key={index} />
      ))}
    </div>
  );
}

function AxisTicks({ count }: { count: number }): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-1 px-1 pt-2" aria-hidden>
      {Array.from({ length: count }).map((_, index) => (
        <span className={`h-2 flex-1 max-w-6 rounded-sm ${styles.shimmer}`} key={index} />
      ))}
    </div>
  );
}

export function ChartSkeleton({ className, height, variant }: ChartSkeletonProps): React.ReactElement {
  const { barGroups, chartRows, gutter, legendLabelWidths, plotGutter } = plotLayout[variant];

  return (
    <div
      className={`grid rounded-md bg-white p-4 ${className ?? ''}`}
      style={{ gridTemplateRows: chartRows, height }}
      aria-busy="true"
      aria-label="Loading chart"
      role="status"
    >
      <div className="relative" aria-hidden>
        <div className="absolute left-1/2 top-2 flex -translate-x-1/2 items-center gap-6">
          {legendLabelWidths.map((labelWidth, index) => (
            <LegendItem key={index} labelWidth={labelWidth} />
          ))}
        </div>
      </div>

      <div className={`relative min-h-0 ${plotGutter}`}>
        <PlotGrid />
        <div className="absolute inset-0 z-10 flex items-end justify-between gap-1 border-b border-bzgray-200 pb-px">
          {barGroups.map(([buyHeight, sellHeight], index) => (
            <div className="flex h-full flex-1 items-end justify-center gap-px" key={index}>
              <span
                className={`w-3 rounded-t-sm ${styles.shimmer}`}
                style={{ animationDelay: `${index * 50}ms`, height: `${buyHeight}%` }}
              />
              <span
                className={`w-3 rounded-t-sm ${styles.shimmer}`}
                style={{ animationDelay: `${index * 50 + 25}ms`, height: `${sellHeight}%` }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className={gutter}>
        <AxisTicks count={barGroups.length} />
      </div>
      <span className="sr-only">Loading chart</span>
    </div>
  );
}
